import React, { useEffect, useState } from 'react';
import { auth, menuAnalysis } from '../api';

const DashboardPage = ({ user, redirectToAnalysis }) => {
  const [usageStats, setUsageStats] = useState(null);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch user stats and recent analyses when component mounts
    const fetchDashboardData = async () => {
      try {
        // Fetch user stats
        const usageResponse = await auth.getUserUsageStats();
        setUsageStats(usageResponse.data);
        
        // Fetch recent analyses (limited to 5)
        const analysesResponse = await menuAnalysis.getAnalysisHistory();
        if (analysesResponse.data && Array.isArray(analysesResponse.data)) {
          // Sort by date (newest first) and limit to 5
          const sortedAnalyses = analysesResponse.data
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5);
          setRecentAnalyses(sortedAnalyses);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <h2 className="text-base text-emerald-600 font-semibold tracking-wide uppercase">Dashboard</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Welcome back, {user?.full_name || user?.email}!
          </p>
          
          {/* Email verification banner */}
          {user && !user.is_email_verified && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md lg:mx-auto lg:max-w-2xl">
              <div className="flex flex-col items-center text-center">
                <svg className="h-6 w-6 text-yellow-400 mb-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <h3 className="text-sm font-medium text-yellow-800">Email not verified</h3>
                <div className="mt-2 text-sm text-yellow-700 max-w-lg">
                  <p>Please verify your email address to ensure full access to your account. Check your inbox for a verification link or <button onClick={() => window.location.href = '/#resend-verification'} className="font-medium underline">request a new verification email</button>.</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Admin badge */}
          {user && user.is_admin && (
            <div className="mt-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Admin
              </span>
            </div>
          )}
          
          <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            Your AI-powered menu analysis assistant.
          </p>
        </div>

        <div className="mt-12">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {/* Stats Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Your Stats</h3>
                {loading ? (
                  <div className="animate-pulse mt-2 space-y-2">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-200 rounded w-4/6"></div>
                  </div>
                ) : (
                  <dl className="mt-2 space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-500">Total Analyses</dt>
                      <dd className="text-sm font-medium text-gray-900">{usageStats?.total_analyses || 0}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-500">Analyses This Month</dt>
                      <dd className="text-sm font-medium text-gray-900">{usageStats?.analyses_this_month || 0}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-500">Remaining Credits</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        {usageStats?.remaining_credits === null ? 'Unlimited' : usageStats?.remaining_credits || 0}
                      </dd>
                    </div>
                  </dl>
                )}
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Quick Actions</h3>
                <div className="mt-6">
                  <button
                    onClick={() => redirectToAnalysis()}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                  >
                    New Menu Analysis
                  </button>
                </div>
              </div>
            </div>

            {/* Account Info Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Account</h3>
                <dl className="mt-2 space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="text-sm font-medium text-gray-900">{user?.email}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Plan</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {loading ? (
                        <div className="animate-pulse h-4 bg-gray-200 rounded w-20"></div>
                      ) : (
                        usageStats?.plan_name || 'Free Plan'
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="text-sm font-medium text-emerald-600">Active</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Analyses */}
        <div className="mt-12">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Recent Analyses</h3>
          {loading ? (
            <div className="animate-pulse space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-100 p-4 rounded-lg">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : recentAnalyses.length > 0 ? (
            <div className="space-y-4">
              {recentAnalyses.map((analysis) => (
                <div key={analysis.id} className="bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition cursor-pointer" onClick={() => redirectToAnalysis(analysis.id)}>
                  <h4 className="font-medium text-gray-900">{analysis.restaurant_name || 'Unnamed Restaurant'}</h4>
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-sm text-gray-500">
                      {new Date(analysis.created_at).toLocaleString()}
                    </p>
                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">
                      {analysis.item_count || 0} items
                    </span>
                  </div>
                  {analysis.query_text && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-600">
                        <span className="font-semibold">Request notes:</span>{' '}
                        {analysis.query_text.length > 100
                          ? `${analysis.query_text.substring(0, 100)}...`
                          : analysis.query_text}
                      </p>
                    </div>
                  )}
                  {analysis.tokens_consumed !== undefined && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">
                        <span className="font-semibold">Tokens:</span> {analysis.tokens_consumed.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No recent analyses. Get started with a new analysis!</p>
              <button
                onClick={() => redirectToAnalysis()}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
              >
                Start New Analysis
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
